import { createContext, ReactNode, useContext } from 'react'
import { useFetchFeatureFlags } from 'ClientPortal/src/api/Queries/feature-flags'
import { useSelectedCustomer } from 'ClientPortal/src/context/SelectedCustomerContext'
import * as Sentry from '@sentry/browser'
import invariant from 'tiny-invariant'
import { type CustomerFeatureFlagValues as FeatureFlagValues } from '@einwert/valuation-api-client'

type FeatureFlags = {
    isEnergySummaryEnabled: boolean
    isMarketDataEnabled: boolean
    isMarketDataDemoDataEnabled: boolean
    isMarketDataFromValuationEnabled: boolean
    isComparablesEnabled: boolean
    isActualRentInChartsEnabled: boolean
    isCustomerFineGrainedAuthorizationEnabled: boolean
    isExternalParticipantsEnabled: boolean
    isPlausibilityInternalApprovalEnabled: boolean
    isLocalizationEnabled: boolean
    isFullAppraiserUIViewMode: boolean
}

export type FeatureFlagContextType =
    | ({ isFeatureFlagsPending: false } & FeatureFlags)
    | ({ isFeatureFlagsPending: true } & Partial<FeatureFlags>)

const defaultPendingContext: FeatureFlagContextType = {
    isFeatureFlagsPending: true,
}

export const FeatureFlagsContext = createContext<FeatureFlagContextType>(defaultPendingContext)

export function FeatureFlagsProvider({
    children,
    featureFlags,
    isFeatureFlagsPending,
}: {
    children: ReactNode
    featureFlags: FeatureFlagValues | undefined
    isFeatureFlagsPending: boolean
}) {
    if (featureFlags) {
        Sentry.setContext('flags', featureFlags)
    }

    const mappedFlags: FeatureFlagContextType = isFeatureFlagsPending
        ? {
              isFeatureFlagsPending: true,
          }
        : {
              isFeatureFlagsPending: false,
              isEnergySummaryEnabled: !!featureFlags?.enable_energy_summary,
              isMarketDataEnabled: !!featureFlags?.enable_market_data,
              isMarketDataDemoDataEnabled: !!featureFlags?.enable_market_data_demo_data,
              isMarketDataFromValuationEnabled: !!featureFlags?.enable_market_data_from_valuation,
              isComparablesEnabled: !!featureFlags?.enable_comparables_for_customers,
              isActualRentInChartsEnabled: !!featureFlags?.enable_actual_rent_in_charts,
              isCustomerFineGrainedAuthorizationEnabled: !!featureFlags?.enable_customer_fine_grained_authorization,
              isExternalParticipantsEnabled: !!featureFlags?.enable_external_participants,
              isPlausibilityInternalApprovalEnabled: !!featureFlags?.enable_plausibility_internal_approval,
              isLocalizationEnabled: !!featureFlags?.enable_localization,
              isFullAppraiserUIViewMode: !!featureFlags?.enable_full_appraiser_ui_view_mode,
          }

    return <FeatureFlagsContext.Provider value={mappedFlags}>{children}</FeatureFlagsContext.Provider>
}

export function WrappedFeatureFlagsProvider({ children, renderPending }: { children: ReactNode; renderPending: () => ReactNode }) {
    const { selectedCustomer } = useSelectedCustomer()
    const { featureFlags, isFeatureFlagsPending } = useFetchFeatureFlags({ customerId: selectedCustomer?.uuid })

    return (
        <FeatureFlagsProvider featureFlags={featureFlags} isFeatureFlagsPending={isFeatureFlagsPending}>
            {isFeatureFlagsPending ? renderPending() : children}
        </FeatureFlagsProvider>
    )
}

export function usePendingFlags(): FeatureFlagContextType {
    const context = useContext(FeatureFlagsContext)
    invariant(context !== undefined, 'usePendingFlags must be used within a FeatureFlagsProvider')
    return context
}

export function useFlags(): FeatureFlags & { isFeatureFlagsPending: false } {
    const context = useContext(FeatureFlagsContext)
    invariant(context !== undefined, 'useFlags must be used within a FeatureFlagsProvider')
    invariant(
        !context.isFeatureFlagsPending,
        'useFlags can only be used in places where feature flags are already loaded. Otherwise use `usePendingFlags`'
    )
    return context as FeatureFlags & { isFeatureFlagsPending: false }
}
